/**
 * Common Types for DataLayer Tracking
 */

/**
 * Standard GA/GTM event names
 */
export enum GaEventType {
    FormInteraction = 'form_interaction',
    FormStart = 'form_start',
    FormSuccess = 'form_success',
    PageView = 'page',
}

/**
 * Standard Form Interaction types
 */
export enum FormInteractionType {
    VIEW = 'view',
    SUBMIT = 'submit',
    RETURN = 'return',
    ERROR = 'error',
    SKIP = 'skip',
    WARNING = 'warning',
}

/**
 * Form interaction types that require form_value data
 * These interactions should include the form data in the event
 */
export const FORM_INTERACTIONS_WITH_VALUE: readonly FormInteractionType[] = [
    FormInteractionType.SUBMIT,
    FormInteractionType.ERROR,
    FormInteractionType.WARNING,
] as const;

/**
 * Form interaction types that don't require form_value data
 * These are typically navigation or view events
 */
export const FORM_INTERACTIONS_WITHOUT_VALUE: readonly FormInteractionType[] = [
    FormInteractionType.VIEW,
    FormInteractionType.RETURN,
    FormInteractionType.SKIP,
] as const;

/**
 * Helper function to check if a form interaction type requires value data
 * @param interactionType The interaction type to check
 * @returns true if the interaction requires form_value data
 */
export function requiresFormValue(interactionType: FormInteractionType): boolean {
    return FORM_INTERACTIONS_WITH_VALUE.includes(interactionType);
}

/**
 * Helper function to check if a form interaction type should not have value data
 * @param interactionType The interaction type to check
 * @returns true if the interaction should not include form_value data
 */
export function shouldOmitFormValue(interactionType: FormInteractionType): boolean {
    return FORM_INTERACTIONS_WITHOUT_VALUE.includes(interactionType);
}

/**
 * Represents different business units within an organization.
 * This enumeration is used to categorize various operational divisions.
 *
 * Enum members:
 * - General: Represents general-purpose services.
 * - Home: Represents home-related services.
 * - Move: Represents a relocation or moving services business unit.
 */
export enum BusinessUnit {
    General = 'general',
    Home = 'home',
    Move = 'move',
}

/**
 * Standard form names used across applications
 */
export enum FormName {
    BusinessRegistration = 'business_registration',
    ProspectForm = 'prospect_form',
    ConsumerQuoteRequest = 'consumer_quote_request',
    Contact = 'contact',
}

/**
 * Base event interface for all dataLayer events
 */
export interface BaseEvent {
    ab_test_variant?: string | null;
    event: GaEventType;
    eventCallback?: () => void;
}

